import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogApi } from '../../services/api';
import Icon from 'components/AppIcon';
import Logo from 'components/Logo';

const CATEGORIES = ['All', 'Dividend Investing', 'DRIP & Compounding', 'Options Income', 'Portfolio Strategy', 'Platform Updates'];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function PostCard({ post, featured = false }) {
  if (featured) {
    return (
      <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none', display: 'block', borderRadius: 16, overflow: 'hidden', border: '1px solid #E2E8F0', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', transition: 'all 0.2s', marginBottom: 32 }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.1)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.05)'}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          <div style={{ background: post.featured_image ? `url(${post.featured_image}) center/cover` : 'linear-gradient(135deg,#2E7D32,#4CAF50)', minHeight: 300 }} />
          <div style={{ padding: '36px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {post.category && <span style={{ fontSize: 11, fontWeight: 700, background: '#e8f5e9', color: '#2E7D32', padding: '3px 10px', borderRadius: 20, display: 'inline-block', marginBottom: 12, width: 'fit-content' }}>{post.category}</span>}
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0F172A', lineHeight: 1.25, marginBottom: 12 }}>{post.title}</h2>
            {post.excerpt && <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7, marginBottom: 20 }}>{post.excerpt}</p>}
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#94A3B8' }}>
              <span>{formatDate(post.published_at)}</span>
              <span>·</span>
              <span>{post.read_time} min read</span>
            </div>
            <div style={{ marginTop: 20, fontSize: 13, fontWeight: 700, color: '#2E7D32', display: 'flex', alignItems: 'center', gap: 6 }}>Read Article →</div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', borderRadius: 14, overflow: 'hidden', border: '1px solid #E2E8F0', background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.04)', transition: 'all 0.2s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.09)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = ''; }}>
      <div style={{ height: 180, background: post.featured_image ? `url(${post.featured_image}) center/cover` : 'linear-gradient(135deg,#e8f5e9,#bbdefb)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!post.featured_image && <span style={{ fontSize: 40 }}>📰</span>}
      </div>
      <div style={{ padding: '20px 20px 22px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {post.category && <span style={{ fontSize: 11, fontWeight: 700, background: '#e8f5e9', color: '#2E7D32', padding: '2px 9px', borderRadius: 20, display: 'inline-block', width: 'fit-content' }}>{post.category}</span>}
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0F172A', lineHeight: 1.4, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.title}</h3>
        {post.excerpt && <p style={{ fontSize: 13, color: '#64748B', lineHeight: 1.65, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.excerpt}</p>}
        <div style={{ marginTop: 'auto', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #F1F5F9' }}>
          <span style={{ fontSize: 11, color: '#94A3B8' }}>{formatDate(post.published_at)}</span>
          <span style={{ fontSize: 11, color: '#94A3B8' }}>{post.read_time} min read</span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogListPage() {
  const [posts, setPosts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [category, setCategory] = useState('All');
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);

  // SEO meta tags for the blog index
  useEffect(() => {
    document.title = 'Blog — Dividend Investing, DRIP & Options Income | Safe Dividend Tracker';
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const desc = 'Actionable guides on dividend investing, DRIP compounding, covered calls, cash-secured puts and portfolio strategy for income investors.';
    setMeta('description', desc);
    setMeta('og:title',       'Blog — Dividend Investing & Options Income | Safe Dividend Tracker', true);
    setMeta('og:description', desc, true);
    setMeta('og:type',        'website', true);
    setMeta('og:url',         'https://safedividendtracker.com/blog', true);
    setMeta('twitter:title',       'Blog | Safe Dividend Tracker');
    setMeta('twitter:description', desc);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', 'https://safedividendtracker.com/blog');

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title:    document.title,
        page_location: window.location.href,
        page_path:     '/blog',
      });
    }

    return () => { document.title = 'Safe Dividend Tracker'; };
  }, []);

  const LIMIT = 9;

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: LIMIT };
    if (category !== 'All') params.category = category;
    blogApi.getPosts(params)
      .then(res => {
        setPosts(res.data?.data || []);
        setTotal(res.data?.pagination?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, page]);

  const handleCategory = (cat) => { setCategory(cat); setPage(1); };

  const [featured, ...rest] = posts;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Navbar ── */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '0 48px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <Logo size={36} />
          <span style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', fontFamily: 'var(--font-heading)' }}>Safe Dividend Tracker</span>
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          {[['Features','/#features'],['Pricing','/#pricing'],['Blog','/blog'],['FAQ','/#faq'],['Contact','/contact'],['Privacy','/privacy-policy']].map(([label, href]) => (
            <Link key={label} to={href} style={{ fontSize: 14, color: label === 'Blog' ? '#2E7D32' : '#64748B', fontWeight: label === 'Blog' ? 700 : 500, textDecoration: 'none', fontFamily: 'var(--font-caption)' }}>{label}</Link>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/login" style={{ fontSize: 14, color: '#475569', fontWeight: 500, textDecoration: 'none', padding: '8px 16px', border: '1px solid #E2E8F0', borderRadius: 8, fontFamily: 'var(--font-caption)' }}>Sign In</Link>
          <Link to="/register" style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#2E7D32,#388E3C)', padding: '8px 18px', borderRadius: 8, textDecoration: 'none', fontFamily: 'var(--font-caption)', boxShadow: '0 2px 8px rgba(46,125,50,0.3)' }}>Get Started Free</Link>
        </div>
      </header>

      {/* ── Hero ── */}
      <div style={{ background: 'linear-gradient(to bottom, #f0fdf4, #fff)', padding: '60px 48px 40px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e8f5e9', border: '1px solid #bbf7d0', color: '#2E7D32', fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 20, marginBottom: 18 }}>
          ✍️ The Dividend Investor's Blog
        </div>
        <h1 style={{ fontSize: 44, fontWeight: 900, color: '#0F172A', margin: '0 0 14px', letterSpacing: '-0.03em', lineHeight: 1.15 }}>
          Insights on Dividend Income,<br />DRIP Compounding & Options
        </h1>
        <p style={{ fontSize: 17, color: '#64748B', maxWidth: 520, margin: '0 auto' }}>
          Actionable guides, strategies and platform updates to help you build reliable passive income.
        </p>
      </div>

      {/* ── Category filters ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 24px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {CATEGORIES.map(cat => (
          <button key={cat} onClick={() => handleCategory(cat)} style={{ padding: '7px 18px', borderRadius: 20, border: `1px solid ${category === cat ? '#2E7D32' : '#E2E8F0'}`, background: category === cat ? '#e8f5e9' : '#fff', color: category === cat ? '#2E7D32' : '#64748B', fontSize: 13, fontWeight: category === cat ? 700 : 500, cursor: 'pointer', transition: 'all 0.15s' }}>
            {cat}
          </button>
        ))}
      </div>

      {/* ── Posts ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 80px' }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid #E2E8F0', background: '#fff' }}>
                <div style={{ height: 180, background: '#F1F5F9', animation: 'pulse 1.5s infinite' }} />
                <div style={{ padding: 20 }}>
                  <div style={{ height: 12, background: '#F1F5F9', borderRadius: 6, marginBottom: 10, width: '40%' }} />
                  <div style={{ height: 16, background: '#F1F5F9', borderRadius: 6, marginBottom: 8 }} />
                  <div style={{ height: 16, background: '#F1F5F9', borderRadius: 6, width: '70%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>No posts yet</h3>
            <p style={{ color: '#64748B', fontSize: 14 }}>{category !== 'All' ? 'No posts in this category yet.' : 'Check back soon — new articles are on the way!'}</p>
          </div>
        ) : (
          <>
            {/* Featured post (first, full width) */}
            {page === 1 && featured && <PostCard post={featured} featured />}

            {/* Grid of remaining posts */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
              {(page === 1 ? rest : posts).map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 48 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>← Prev</button>
                {[...Array(totalPages)].map((_, i) => (
                  <button key={i} onClick={() => setPage(i + 1)} style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${page === i+1 ? '#2E7D32' : '#E2E8F0'}`, background: page === i+1 ? '#2E7D32' : '#fff', color: page === i+1 ? '#fff' : '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{i + 1}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── CTA ── */}
      <div style={{ background: '#0F172A', padding: '60px 24px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 12 }}>Put your dividend strategy to work</h2>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', marginBottom: 28 }}>Track dividends, forecast DRIP growth, and manage your income portfolio for free.</p>
        <Link to="/register" style={{ display: 'inline-block', padding: '12px 28px', borderRadius: 10, background: '#22C55E', color: '#fff', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>Start Free — No Credit Card</Link>
      </div>
    </div>
  );
}
